import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '@/shared/layout';
import { Button, Filter, Search, KPICard, Card, Badge } from '@/components/ui';
import { useLogs } from './hooks/useLogs';
import { useAuth } from '@/core/auth/AuthContext';
import { AuthButton } from '@/core/permissions/AuthButton';
import { LogTable } from './components/LogTable';
import { LogFormModal } from './components/LogFormModal';
import type { MaintenanceLog, LogStatus } from './types/maintenance-logs.types';

const statusLabels: Record<LogStatus, string> = {
  draft: 'Bản nháp',
  submitted: 'Chờ duyệt',
  approved: 'Đã duyệt',
  rejected: 'Từ chối',
};

export default function LogListPage() {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const navigate = useNavigate();
  
  const [showCreate, setShowCreate] = useState(false);
  const [editItem, setEditItem] = useState<MaintenanceLog | null>(null);

  const {
    logs,
    total,
    stats,
    isLoading,
    filters,
    setFilters,
  } = useLogs(companyId);

  return (
    <PageLayout
      title="Nhật ký bảo dưỡng"
      description="Ghi nhận khối lượng thực tế, hình ảnh hiện trường và thời gian triển khai của đội ngũ bảo dưỡng."
      breadcrumbs={[{ label: 'Bảo dưỡng' }, { label: 'Nhật ký' }]}
      isLoading={isLoading}
      headerActions={
        <div className="flex gap-2">
          <Button variant="outline" icon="ri-download-line">Xuất báo cáo</Button>
          <AuthButton 
            module="maintenance" 
            action="create" 
            variant="primary" 
            icon="ri-add-line" 
            onClick={() => setShowCreate(true)}
          >
            Ghi nhật ký mới
          </AuthButton>
        </div>
      }
    >
      <div className="space-y-6">
        {/* KPI Board */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            title="Nhật ký hôm nay"
            value={stats?.todayLogs || 0}
            icon="ri-calendar-check-line"
            variant="info"
          />
          <KPICard
            title="Tổng khối lượng"
            value={stats?.completedQuantity || 0}
            icon="ri-bubble-chart-line"
            variant="success"
            suffix=" m2/gốc"
          />
          <KPICard
            title="Tổng giờ công"
            value={stats?.totalWorkHours || 0}
            icon="ri-time-line"
            variant="warning"
            suffix=" h"
          />
          <KPICard
            title="Tỉ lệ duyệt"
            value={Math.round(stats?.approvalRate || 0)}
            icon="ri-shield-check-line"
            variant="primary"
            suffix="%"
          />
        </div>

        <PageLayout.Toolbar>
          <div className="flex flex-col md:flex-row gap-4 w-full justify-between items-start md:items-center">
            <div className="flex flex-wrap gap-3 flex-1">
              <Search
                placeholder="Tìm nội dung công việc..."
                value={filters.search}
                onChange={(v) => setFilters({ ...filters, search: v })}
                className="w-full md:w-80"
              />
              <Filter
                options={[
                  {
                    id: 'status', label: 'Trạng thái', type: 'select', choices: [
                      { value: 'all', label: 'Tất cả trạng thái' },
                      ...Object.entries(statusLabels).map(([val, label]) => ({ value: val, label }))
                    ]
                  }
                ]}
                onFilterChange={(k, v) => setFilters({ ...filters, [k]: v })}
              />
            </div>
          </div>
        </PageLayout.Toolbar>

        <Card className="overflow-hidden border-none shadow-sm">
          <LogTable 
            data={logs} 
            isLoading={isLoading} 
            onEdit={(item) => setEditItem(item)}
            onRowClick={(item) => navigate(`./detail/${item.id}`)} 
          />
        </Card>
      </div>

      <LogFormModal
        isOpen={showCreate || !!editItem}
        item={editItem}
        companyId={companyId}
        onClose={() => {
          setShowCreate(false);
          setEditItem(null);
        }}
      />
    </PageLayout>
  );
}
